import { useCallback, useMemo } from "react";
import { useLocalStorage } from "../../../../hooks/useLocalStorage";
import { useParker } from "../../../../providers/parker";

const STORAGE_KEY = "portal_saved_permits";

export type SavedPermit = {
	publicIdentifier: string;
	publicAccessToken: string;
};

type SavedPermits = Record<string, SavedPermit[]>;

export const useSavedPermits = () => {
	const { isLoggedIn } = useParker();
	const [rawSavedPermit, setSavedPermits, removeSavedPermits] = useLocalStorage<SavedPermits>(STORAGE_KEY, {});

	const addPermit = useCallback(
		(locationLookupId: string, permit: SavedPermit) => {
			if (isLoggedIn) {
				return;
			}

			setSavedPermits((prev) => {
				const existing = prev[locationLookupId] ?? [];
				if (existing.some((p) => p.publicIdentifier === permit.publicIdentifier)) {
					return prev;
				}
				return {
					...prev,
					[locationLookupId]: [permit, ...existing],
				};
			});
		},
		[isLoggedIn, setSavedPermits],
	);

	const clearAllPermits = useCallback(() => {
		removeSavedPermits();
	}, [removeSavedPermits]);

	const savedPermits = useMemo(
		() => [
			...new Map(
				Object.values(rawSavedPermit)
					.flat()
					.map((p) => [p.publicIdentifier, p] as const),
			).values(),
		],
		[rawSavedPermit],
	);

	return {
		savedPermits,
		addPermit,
		clearAllPermits,
	};
};
